import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { Message } from '../types';
import api from '../services/api';

interface UseSignalRReturn {
  connectionState: string;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  sendMessage: (method: string, ...args: any[]) => Promise<void>;
  lastMessage: Message | null;
}

export const useSignalR = (): UseSignalRReturn => {
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [messages, setMessages] = useState<Message[]>([]);
  const [lastMessage, setLastMessage] = useState<Message | null>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  useEffect(() => {
    // `cancelled` guards the async connect against unmount/StrictMode double-mount:
    // connect() is async and the connection ref is only set after start(), so a
    // connection that finishes starting AFTER unmount must be stopped explicitly —
    // otherwise it lingers and eats one of the (Free-tier: 20) connection slots.
    let cancelled = false;
    let conn: signalR.HubConnection | null = null;

    const connect = async () => {
      try {
        // Negotiate through the shared axios `api` client so the MSAL bearer +
        // 401-refresh interceptor apply — the negotiate endpoint now requires an
        // authenticated principal (D3), so a raw unauthenticated fetch would be
        // rejected. api.post returns the parsed connection info as `data`.
        let connectionInfo: any;
        try {
          const resp = await api.post('/signalr_negotiate');
          connectionInfo = resp.data;
        } catch (e) {
          if (cancelled) return;
          console.error('Failed to negotiate SignalR connection:', e);
          setConnectionState('error');
          return;
        }
        if (cancelled) return;

        const connection = new signalR.HubConnectionBuilder()
          .withUrl(connectionInfo.url, {
            accessTokenFactory: () => connectionInfo.accessToken
          })
          .withAutomaticReconnect()
          .configureLogging(signalR.LogLevel.Warning)
          .build();
        conn = connection;

        connection.on('newMessage', (message: Message) => setLastMessage(message));
        // Status/assignment changes broadcast as updateMessage; same upsert path.
        connection.on('updateMessage', (message: Message) => setLastMessage(message));

        connection.onreconnecting(() => setConnectionState('reconnecting'));
        connection.onreconnected(() => setConnectionState('connected'));
        connection.onclose(() => setConnectionState('disconnected'));

        await connection.start();
        if (cancelled) {
          // Unmounted while starting — release the slot immediately.
          connection.stop().catch(() => {});
          return;
        }
        setConnectionState('connected');
        connectionRef.current = connection;
      } catch (error) {
        console.error('SignalR connection error:', error);
        setConnectionState('error');
      }
    };

    connect();

    // Proactively release the connection when the tab is closed/hidden so the
    // (Free-tier) connection slot frees right away instead of waiting for the
    // service to time the dropped socket out — this is what lets rapid refreshes
    // stop piling connections up toward the 20-connection cap.
    const release = () => { try { conn?.stop(); } catch { /* best-effort */ } };
    window.addEventListener('pagehide', release);

    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', release);
      const c = connectionRef.current || conn;
      if (c) c.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, []);

  const sendMessage = useCallback(async (method: string, ...args: any[]) => {
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      await connectionRef.current.invoke(method, ...args);
    }
  }, []);

  return {
    connectionState,
    messages,
    setMessages,
    sendMessage,
    lastMessage
  };
};

export default useSignalR;
